import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';

import { signOut } from '../services/auth_service';
import { useTheme } from '../contexts/theme_context';

const DEFAULT_BREAKPOINT = 768;

// Client org names are stored ALL-CAPS; render them Title Case like the other tabs
const toTitleCase = value =>
	String(value)
		.toLowerCase()
		.replace(/\b\w/g, char => char.toUpperCase());

const SideBarComponent = ({ menuItems, defaultActiveKey, breakpoint = DEFAULT_BREAKPOINT }) => {
	const safeMenuItems = useMemo(() => (Array.isArray(menuItems) ? menuItems : []), [menuItems]);
	const navigate = useNavigate();
	const location = useLocation();
	const [isOpen, setIsOpen] = useState(false);
	const firstKey = useMemo(
		() => defaultActiveKey ?? safeMenuItems[0]?.key ?? null,
		[defaultActiveKey, safeMenuItems]
	);
	const [activeKey, setActiveKey] = useState(firstKey);
	const [expandedGroups, setExpandedGroups] = useState(() => new Set());
	const { isDarkMode, toggleTheme, isSavingTheme, isLoadingThemePreference } = useTheme();

	const isChildActive = child =>
		Boolean(child.path) &&
		(location.pathname === child.path || location.pathname.startsWith(`${child.path}/`));

	const toggleGroup = key =>
		setExpandedGroups(prev => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});

	useEffect(() => {
		const handleResize = () => setIsOpen(window.innerWidth >= breakpoint);
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [breakpoint]);

	useEffect(() => {
		if (!safeMenuItems || safeMenuItems.length === 0) return;

		const match = safeMenuItems.find(item => {
			if (!item.path) return false;
			const paths = [item.path, ...(item.aliases || [])];
			return paths.some(p => {
				const prefix = `${p}/`;
				return location.pathname === p || location.pathname.startsWith(prefix);
			});
		});

		if (match) {
			if (activeKey !== match.key) setActiveKey(match.key);
			return;
		}

		// A group child owns the current route (e.g. a client dashboard), so no
		// top-level item should stay highlighted alongside it.
		const onChildRoute = safeMenuItems.some(
			item =>
				Array.isArray(item.children) &&
				item.children.some(
					child =>
						Boolean(child.path) &&
						(location.pathname === child.path ||
							location.pathname.startsWith(`${child.path}/`))
				)
		);
		if (onChildRoute) {
			if (activeKey !== null) setActiveKey(null);
			return;
		}

		if (defaultActiveKey && activeKey !== defaultActiveKey) {
			setActiveKey(defaultActiveKey);
		}
	}, [location.pathname, safeMenuItems, defaultActiveKey, activeKey]);

	// Auto-expand a group when the current route matches one of its children
	useEffect(() => {
		const group = safeMenuItems.find(
			item => Array.isArray(item.children) && item.children.some(isChildActive)
		);
		if (group) {
			setExpandedGroups(prev => (prev.has(group.key) ? prev : new Set(prev).add(group.key)));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, safeMenuItems]);

	useEffect(() => {
		if (isOpen && window.innerWidth < breakpoint) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
	}, [isOpen, breakpoint]);

	const handleClick = item => {
		if (item.path) {
			navigate(item.path);
			if (window.innerWidth < breakpoint) setIsOpen(false);
			return;
		}
		if (item.key === activeKey) return;
		setActiveKey(item.key);
		if (window.innerWidth < breakpoint) setIsOpen(false);
	};

	const handleSignOut = async () => {
		try {
			await signOut();
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('Sign out failed', err);
		} finally {
			navigate('/');
			if (window.innerWidth < breakpoint) setIsOpen(false);
		}
	};

	const handleToggleTheme = async () => {
		try {
			await toggleTheme();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Theme toggle failed', error);
		}
	};

	return (
		<div className={isOpen ? 'sidebar--open' : ''}>
			<style>{`
        :root {
          --sb-width: 232px;
          --header-h: 51px;
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          width: var(--sb-width);
          height: 100vh;
          background: var(--sb-header-bg);
          color: var(--sb-text);
          border-right: 1px solid var(--sb-border);
          z-index: 1000;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .sidebar__panel {
          background: var(--sb-header-bg);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          padding: 14px 16px;
          margin-bottom: 20px;
        }

        .sidebar__title {
          margin: 0;
          text-align: center;
          font-weight: 800;
          color: var(--sb-text-strong);
        }

        .sidebar__nav {
          display: grid;
          gap: 6px;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 0px 10px 10px 0px;
          cursor: pointer;
          color: var(--sb-text);
          text-decoration: none;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          font-size: inherit;
          font-family: inherit;
        }
        .menu-item:hover { background: var(--sb-hover); width: 90% }
        .menu-item--active {
          background: var(--sb-active);
          color: var(--sb-text-strong);
          font-weight: 700;
          width: 90%;
        }

        /* Clients group header keeps full width on hover so its caret stays put */
        .menu-item--group:hover { width: 100%; }

        .sidebar__nav .divider-before {
          margin-top: 0px;
          border-top: 1px solid rgba(255,255,255,0.10);
        }

        .menu-item__caret {
          margin-left: auto;
          display: flex;
          align-items: center;
          font-size: 15px;
          color: var(--sb-text);
          opacity: 0.9;
        }

        .menu-item__children {
          max-height: 170px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.35) transparent;
        }
        .menu-item__children::-webkit-scrollbar {
          width: 8px;
        }
        .menu-item__children::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.35);
          border-radius: 4px;
        }
        .menu-item__children::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.55);
        }

        .menu-item__icon {
          width: 20px;
          display: inline-flex;
          justify-content: center;
        }

        .menu-item__icon--animated {
          animation: floatForward 2s ease-in-out infinite;
        }

        @keyframes floatForward {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(4px);
          }
        }

        .sidebar-toggle {
          position: fixed;
          top: 10px;
          left: 8px;
          background: transparent;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          z-index: 1100;
          color: #111827;
        }

        .sidebar--open .sidebar-toggle {
          display: none;
        }

        @media (min-width: 768px) {
          .sidebar-toggle {
            display: none;
          }
        }

        @media (max-width: 767px) {
          .menu-item {
            font-size: 14px;
          }
            .sidebar__panel button img{
            width: 140px;}
        }

        .overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
          display: none;
        }

        .overlay--visible { display: block; }

        .sidebar__close-btn {
          position: absolute;
          top: 0;
          right: 0;
          background: transparent;
          border: none;
          font-size: 1.5rem;
          color: #ffffff;
          cursor: pointer;
          z-index: 1001;
        }

        @media (max-width: 767px) {
          .sidebar__close-btn {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }

        .sidebar__footer {
          padding: 16px 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
          justify-content: center;
          position: relative;
          width: 100%;
        }
        .sidebar__footer::before {
          content: '';
          display: block;
          width: 90%;
          height: 2px;
          background: rgba(255,255,255,0.10);
          margin-bottom: 12px;
          border-radius: 2px;
        }
        .sidebar__footer .signout-btn {
          width: 90%;
          background: transparent;
          color: var(--sb-text);
          padding: 10px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          justify-content: center;
        }
        .sidebar__footer .signout-btn:hover {
          background: rgba(255,255,255,0.02);
          color: var(--sb-text-strong);
          border-color: rgba(255,255,255,0.10);
        }

        .sidebar__footer .theme-toggle-btn {
          width: 90%;
          background: rgba(255, 255, 255, 0.06);
          color: var(--sb-text-strong);
          border: 1px solid rgba(255, 255, 255, 0.12);
          padding: 10px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .sidebar__footer .theme-toggle-btn:hover {
          background: rgba(255, 255, 255, 0.14);
        }

        .sidebar__footer .theme-toggle-btn:disabled {
          opacity: 0.6;
          cursor: wait;
        }

        .sidebar__footer .theme-toggle-icon {
          width: 16px;
          height: 16px;
          display: inline-block;
          fill: currentColor;
          flex: 0 0 16px;
        }
        .sidebar__footer .signout-icon {
          width: 18px;
          height: 18px;
          display: inline-block;
          fill: currentColor;
          flex: 0 0 18px;
        }

        /* Note: we no longer hide .sidebar-toggle on desktop to keep it in the a11y tree */
        @media (min-width: ${breakpoint}px) {
          .content { margin-left: var(--sb-width); }
          .overlay { display: none; }
          .sidebar__close-btn { display: none; }
        }
      `}</style>

			{/* Hamburger button: always rendered & accessible */}
			<button
				className='sidebar-toggle'
				onClick={() => setIsOpen(prev => !prev)}
				aria-label='Toggle sidebar'
				aria-expanded={isOpen}
			>
				☰
			</button>

			{/* Dark overlay */}
			<div
				className={`overlay ${isOpen ? 'overlay--visible' : ''}`}
				onClick={() => setIsOpen(false)}
				aria-hidden='true'
			/>

			{/* Sidebar */}
			<div className='sidebar' style={{ display: isOpen ? 'flex' : 'none' }}>
				{/* Close button (mobile only when open) */}
				{isOpen && window.innerWidth < breakpoint && (
					<button
						className='sidebar__close-btn'
						onClick={() => setIsOpen(false)}
						aria-label='Close sidebar'
					>
						&times;
					</button>
				)}

				<div>
					<div className='sidebar__panel'>
						<button
							type='button'
							onClick={() => navigate('/')}
							style={{
								background: 'transparent',
								border: 'none',
								padding: 0,
								cursor: 'pointer',
								display: 'block',
								margin: '0 auto',
							}}
							aria-label='Go to home page'
						>
							<img
								src='/conversio_logo_white.png'
								alt='Conversio Brand'
								style={{ width: '170px' }}
								className='object-contain block relative z-20 pointer-events-auto'
							/>
						</button>
					</div>

					<nav className='sidebar__nav'>
						{safeMenuItems.map(item =>
							Array.isArray(item.children) ? (
								<div
									key={item.key || item.label}
									className={item.dividerBefore ? 'divider-before' : undefined}
								>
									<button
										className='menu-item menu-item--group'
										onClick={() => toggleGroup(item.key)}
										aria-expanded={expandedGroups.has(item.key)}
									>
										<span className='menu-item__icon'>{item.icon}</span>
										<span>{item.label}</span>
										<span className='menu-item__caret'>
											{expandedGroups.has(item.key) ? (
												<FaChevronDown />
											) : (
												<FaChevronRight />
											)}
										</span>
									</button>
									{expandedGroups.has(item.key) && (
										<div className='menu-item__children'>
											{item.children.length === 0 ? (
												<span
													className='menu-item'
													style={{ opacity: 0.6, cursor: 'default' }}
												>
													<span>{item.emptyLabel || 'None'}</span>
												</span>
											) : (
												item.children.map(child => (
													<button
														key={child.key}
														className={`menu-item ${isChildActive(child) ? 'menu-item--active' : ''}`}
														onClick={() => handleClick(child)}
														aria-pressed={isChildActive(child)}
													>
														<span>{toTitleCase(child.label)}</span>
													</button>
												))
											)}
										</div>
									)}
								</div>
							) : (
								<button
									key={item.key || item.label}
									className={`menu-item ${activeKey === item.key ? 'menu-item--active' : ''} ${item.dividerBefore ? 'divider-before' : ''}`}
									onClick={() => handleClick(item)}
									aria-pressed={activeKey === item.key}
								>
									<span
										className={`menu-item__icon ${item.label === 'Back to App' ? 'menu-item__icon--animated' : ''}`}
									>
										{item.icon}
									</span>
									<span>{item.label}</span>
								</button>
							)
						)}
					</nav>
				</div>

				<div className='sidebar__footer'>
					<button
						type='button'
						className='theme-toggle-btn'
						onClick={handleToggleTheme}
						disabled={isSavingTheme || isLoadingThemePreference}
						aria-label='Toggle dark mode'
					>
						<svg
							className='theme-toggle-icon'
							viewBox='0 0 24 24'
							aria-hidden='true'
							focusable='false'
						>
							{isDarkMode ? (
								<path d='M12 4a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1zm0 13a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1zm8-6a1 1 0 1 1 0 2h-1a1 1 0 1 1 0-2h1zM6 12a1 1 0 0 1-1 1H4a1 1 0 1 1 0-2h1a1 1 0 0 1 1 1zm10.95-5.536a1 1 0 0 1 1.414 0l.707.707a1 1 0 0 1-1.414 1.415l-.707-.708a1 1 0 0 1 0-1.414zM6.343 17.071a1 1 0 0 1 1.414 0l.707.708a1 1 0 0 1-1.414 1.414l-.707-.707a1 1 0 0 1 0-1.415zm12.728.707a1 1 0 0 1 0 1.414l-.707.707a1 1 0 0 1-1.414-1.414l.707-.708a1 1 0 0 1 1.414 0zM7.757 6.464a1 1 0 0 1 0 1.414l-.707.708A1 1 0 1 1 5.636 7.17l.707-.707a1 1 0 0 1 1.414 0zM12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8z' />
							) : (
								<path d='M12.74 2.01a1 1 0 0 1 .77 1.27 8 8 0 0 0 7.22 10.53 1 1 0 0 1 .68 1.69A10 10 0 1 1 10.5 1.34a1 1 0 0 1 1.24.67 1 1 0 0 1 .06 0h.94z' />
							)}
						</svg>
						<span>{isDarkMode ? 'Switch to Light' : 'Switch to Dark'}</span>
					</button>
					<button
						type='button'
						className='signout-btn'
						onClick={handleSignOut}
						aria-label='Sign Out'
					>
						<svg
							className='signout-icon'
							viewBox='0 0 24 24'
							aria-hidden='true'
							focusable='false'
						>
							<path d='M16 13v-2H7V8l-5 4 5 4v-3zM20 3h-8v2h8v14h-8v2h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z' />
						</svg>
						<span>Sign Out</span>
					</button>
				</div>
			</div>
		</div>
	);
};

SideBarComponent.propTypes = {
	menuItems: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string,
			label: PropTypes.string.isRequired,
			icon: PropTypes.node,
			content: PropTypes.node,
			path: PropTypes.string,
			aliases: PropTypes.arrayOf(PropTypes.string),
			emptyLabel: PropTypes.string,
			dividerBefore: PropTypes.bool,
			children: PropTypes.arrayOf(
				PropTypes.shape({
					key: PropTypes.string,
					label: PropTypes.string,
					path: PropTypes.string,
				})
			),
		})
	),
	defaultActiveKey: PropTypes.string,
	breakpoint: PropTypes.number,
};

export const SideBar = React.memo(SideBarComponent);
